import { ChevronDown, X } from "lucide-react";
import {
  type ChangeEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useInventory } from "../hooks/useInventory";
import { CATEGORIES } from "../lib/categories";
import { UNIT_CATEGORIES, UNIT_CATEGORY_NAMES } from "../lib/units";
import type { FormErrors as ValidationFormErrors } from "../lib/validation";
import { focusFirstInvalidField, validateItemForm } from "../lib/validation";
import type { InventoryItem } from "../services/inventoryService";

interface ItemFormProps {
  onClose: () => void;
  onItemAdded: () => void;
  item?: InventoryItem;
}

type LocationType = "fridge" | "freezer" | "pantry" | "other";
type CategoryType = (typeof CATEGORIES)[number];

type FormErrors = ValidationFormErrors<"itemName" | "quantity">;

// Modal form for adding or editing an inventory item, handling validation,
// accessibility focus trapping, and submission. When `item` is provided the
// form is pre-filled and submission updates the existing item instead of
// creating a new one.
export default function ItemForm({
  onClose,
  onItemAdded,
  item,
}: ItemFormProps) {
  const { addItem, updateItem } = useInventory("all");
  const isEditMode = !!item;

  const [itemName, setItemName] = useState(item?.item_name ?? "");
  const [quantity, setQuantity] = useState<number>(item?.quantity ?? 1);
  const [unit, setUnit] = useState(item?.unit ?? "each (ea)");
  const [location, setLocation] = useState<LocationType>(
    item?.location ?? "fridge",
  );
  const [category, setCategory] = useState<CategoryType>(
    (item?.category as CategoryType) ?? "produce",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const modalRef = useRef<HTMLDivElement>(null);
  const itemNameRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);

  useFocusTrap(modalRef, onClose);

  useEffect(() => {
    if (!isEditMode) {
      itemNameRef.current?.focus();
    }
  }, [isEditMode]);

  // Handlers clear relevant field errors as user updates inputs
  const handleItemNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setItemName(e.target.value);
    if (formErrors.itemName)
      setFormErrors((prev) => ({ ...prev, itemName: undefined }));
    if (error) setError("");
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const num = val === "" ? 0 : Number(val);
    setQuantity(num);
    if (formErrors.quantity)
      setFormErrors((prev) => ({ ...prev, quantity: undefined }));
    if (error) setError("");
  };

  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setUnit(e.target.value);
  };

  const handleLocationClick = (loc: LocationType) => () => setLocation(loc);
  const handleCategoryClick = (cat: CategoryType) => () => setCategory(cat);

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Run validation via the extracted helper
    const errors = validateItemForm(itemName, quantity);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Focus first invalid field via extracted helper (pass refs object and explicit order)
      focusFirstInvalidField<"itemName" | "quantity">(
        errors,
        { itemName: itemNameRef, quantity: quantityRef },
        ["itemName", "quantity"],
      );
      return;
    }

    setFormErrors({});
    setError("");
    setLoading(true);

    try {
      if (isEditMode) {
        await updateItem({
          id: item.id,
          updates: {
            item_name: itemName.trim(),
            quantity,
            unit,
            location,
            category,
          },
        });
        toast.success(`"${itemName.trim()}" updated`);
      } else {
        await addItem({
          item_name: itemName.trim(),
          quantity,
          unit,
          location,
          category,
        });

        const locationName =
          location.charAt(0).toUpperCase() + location.slice(1);
        toast.success(`"${itemName.trim()}" added to ${locationName}`);
      }
      onItemAdded();
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? "update" : "add"} item.`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="pt-safe fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2
            id="modal-title"
            className="text-xl font-bold text-gray-800 dark:text-gray-100"
          >
            {isEditMode ? "Edit Item" : "Add to Inventory"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close dialog"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="itemName"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Item Name{" "}
              <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              ref={itemNameRef}
              id="itemName"
              type="text"
              value={itemName}
              onChange={handleItemNameChange}
              placeholder="e.g. Milk, Chicken, Carrots"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 placeholder-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500"
              aria-describedby={
                formErrors.itemName
                  ? "itemName-error"
                  : error
                    ? "form-error"
                    : undefined
              }
              aria-invalid={!!formErrors.itemName || !!error}
              required
            />
            {formErrors.itemName && (
              <p
                id="itemName-error"
                className="mt-1 text-sm text-red-600 dark:text-red-400"
              >
                {formErrors.itemName}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="quantity"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Quantity{" "}
                <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                ref={quantityRef}
                id="quantity"
                type="number"
                step="0.25"
                min="0"
                value={quantity}
                onChange={handleQuantityChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                aria-describedby={
                  formErrors.quantity ? "quantity-error" : undefined
                }
                aria-invalid={!!formErrors.quantity}
                required
              />
              {formErrors.quantity && (
                <p
                  id="quantity-error"
                  className="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                  {formErrors.quantity}
                </p>
              )}
            </div>

            <div className="relative">
              <label
                htmlFor="unit"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Unit
              </label>
              <select
                id="unit"
                value={unit}
                onChange={handleUnitChange}
                className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              >
                {UNIT_CATEGORY_NAMES.map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {UNIT_CATEGORIES[cat].map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute top-9 right-3 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <fieldset>
            <legend className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Location <span className="text-red-600 dark:text-red-400">*</span>
            </legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(["fridge", "freezer", "pantry", "other"] as const).map(
                (loc) => (
                  <button
                    type="button"
                    key={loc}
                    onClick={handleLocationClick(loc)}
                    className={`rounded-lg px-3 py-2 text-sm font-medium capitalize transition ${
                      location === loc
                        ? "bg-blue-600 text-white dark:bg-blue-500"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    }`}
                  >
                    {loc}
                  </button>
                ),
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Category <span className="text-red-600 dark:text-red-400">*</span>
            </legend>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={handleCategoryClick(cat)}
                  className={`rounded-lg px-3 py-2 text-center text-xs font-medium capitalize transition ${
                    category === cat
                      ? "bg-blue-600 text-white dark:bg-blue-500"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {cat.replace("_", " ")}
                </button>
              ))}
            </div>
          </fieldset>

          {error && (
            <div
              id="form-error"
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {isEditMode
                ? loading
                  ? "Updating..."
                  : "Update Item"
                : loading
                  ? "Adding..."
                  : "Add Item"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
